import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { setMarathonParticipantImagesPage, setMarathonParticipantImagesPageSize } from "api/slice/marathonsSlice/marathonsSlice";
import { CustomPagination } from "components/CustomPagination/CustomPagination";
import { useAppDispatch } from "shared/config/configureStore";
import { useAppSelector } from "shared/lib/hooks/hooks";
import { CloseIcon, SearchIcon } from "assets/icons";
import { Dispatch, FC, SetStateAction, useState } from "react";
import {
  Input,
} from "entities/admin/lib/MUIStyles/MUIStyles";
import { getMarathonParticipantImageByFileId } from "api/thunk/marathonParticipantImages/getActions/getMarathonParticipantImageByFileId";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

const BASE_URL = "https://marathon-app.timelysoft.org/";

interface IAllPhotosProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
}

const AllPhotos: FC<IAllPhotosProps> = ({
  search,
  setSearch
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, loading } = useAppSelector((state) => state.marathons.marathonParticipantImages.response);
  const { page, pageSize } = useAppSelector((state) => state.marathons.marathonParticipantImages.params);
  const { data: imageUrl, loading: imageUrlLoading } = useAppSelector((state) => state.marathonParticipantImages.marathonParticipantImageByFileId.response);
  const handlePageChange = (newPage: number) => {
    dispatch(setMarathonParticipantImagesPage(newPage));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    dispatch(setMarathonParticipantImagesPageSize(newPageSize));
    dispatch(setMarathonParticipantImagesPage(1));
  };

  const handleImageClick = (fileId: string) => {
    dispatch(getMarathonParticipantImageByFileId(fileId.toString()));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box>
      <Box>
        <TextField
          type="search"
          variant="outlined"
          size="small"
          sx={{
            ...Input,
            maxWidth: '500px',
            width: "100%",
            marginBottom: "24px",
            '& .MuiOutlinedInput-root': {
              padding: "1.5px 13px 1.5px 0",
              '&:hover fieldset': {
                borderColor: '#0090D1',
              },
            },
          }}
          placeholder={t("photoGallery.searchPlaceholder")}
          value={search}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <CircularProgress />
        </Box>
      ) : !data?.items?.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <Typography color="text.secondary">{t("photoGallery.empty")}</Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: "12px",
              mb: 2,
            }}
          >
            {data.items.map((item) => (
              <Box
                key={item.imageId}
                onClick={() => handleImageClick(item.imageId)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "8px",
                  overflow: "hidden",
                  height: { xs: "180px", sm: "200px" },
                  "& img": {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    transition: "transform 0.2s ease",
                  },
                  "&:hover img": {
                    transform: "scale(1.03)",
                  },
                }}
              >
                <img
                  src={`${BASE_URL}${item.path}`}
                  alt={item.imageId}
                />
              </Box>
            ))}
          </Box>

          <CustomPagination
            totalCount={data?.totalCount ?? 0}
            page={page ?? 1}
            pageSize={pageSize ?? 10}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fb 100%)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            backdropFilter: "blur(8px)",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 24,
              right: 24,
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent)",
            },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "text.primary",
            }}
          >
            {imageUrl && !imageUrlLoading && (
              <IconButton
                component="a"
                href={imageUrl}
                download="photo.jpg"
                title="Скачать фото"
                sx={{
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  color: "text.secondary",
                  fontSize: "1rem",
                  fontWeight: 500,
                  border: "1.5px solid transparent",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    color: "primary.main",
                    backgroundColor: "rgba(25, 118, 210, 0.06)",
                    border: "1.5px solid rgba(25, 118, 210, 0.2)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.12)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                <DownloadIcon sx={{ fontSize: "1rem" }} />
                {t("photoGallery.downloadPhoto")}
              </IconButton>
            )}
          </DialogTitle>

          <IconButton
            onClick={handleClose}
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              color: "text.secondary",
              border: "1.5px solid transparent",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            backgroundColor: "#fff",
            padding: "20px"
          }}
        >
          {imageUrlLoading || !imageUrl ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                textAlign: "center",
              }}
            >
              <CircularProgress />

              <Typography variant="body2" color="text.secondary">
                {t("photoGallery.loadingImage")}
              </Typography>
            </Box>
          ) : (
            <Box
              component="img"
              src={imageUrl}
              alt="full"
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AllPhotos;